// src/components/frontpage/columns/ModeColumn.jsx
//
// Shared shell for the MultiViewGrid front-page columns (Chat · Events · Swap, and the
// Phase-2 Directory column). Owns the parts that were copy-pasted across the three bespoke
// columns: the centred header row (title + "+" button + reserved ✏ slot), the independent
// scroll container, and the loading/empty slots. Width-agnostic and context-agnostic — it
// makes no Locality-only assumptions, so a future Groups front page can mount it unchanged.
//
// Each column supplies only its title, theme colour, "+" handler, and its own body renderer.
// Three props exist because the real columns genuinely differ (not decoration):
//   - scrollBody={false}  → the child owns its own scroll. Chat passes this because its scroll
//                           lives inside ConcertinaThread; double-wrapping would break the
//                           #/chat/{uuid} permalink auto-scroll.
//   - overlay             → a sibling rendered over the body inside the position:relative outer
//                           (Swap's "Coming Soon" watermark + its create modal). Reusable for
//                           any future locked/loading overlay.
//   - bodyOpacity         → dims the scroll container itself (Swap's coming-soon 0.35). Applied
//                           to the scroll wrapper rather than an inner div so the scrollbar dims
//                           too, preserving the exact current pixels. NOTE: single consumer —
//                           when Swap leaves coming-soon mode this prop likely loses its only
//                           caller and can be removed.
//
// editAction is a reserved ✏ slot for a later phase; it renders nothing in Phase 2.

import React from 'react';
import { Plus, Pencil } from 'lucide-react';
import { useMediaQuery, clampScale } from '../../../hooks/useMediaQuery';

const ModeColumn = ({
  title,
  themeColor,
  hideColumnHeader = false,
  onAdd,                 // renders the "+" only when provided
  addLabel,              // tooltip (title attr) for the "+" button
  editAction,            // RESERVED ✏ slot — unused in Phase 2
  isLoading = false,
  isEmpty = false,
  emptyLabel,
  scrollBody = true,     // false → child owns its own scroll (Chat / ConcertinaThread)
  bodyOpacity = 1,       // single consumer: Swap coming-soon dim — removable when that ends
  overlay = null,        // sibling over the body inside the relative outer (Swap watermark+modal)
  footer = null,         // optional pinned footer below the scroll body (Directory branding)
  containerBg = null,    // optional wash behind the whole column (header+body+footer) — for a
                          // column that must read as visually distinct from its neighbours even
                          // while the ambient ThemeProvider hasn't switched (GroupsColumn's mauve
                          // wash on the still-locality front page). Other columns: unset, transparent.
  containerBorder = null, // optional fine border pairing with containerBg — the wash alone read
                          // as too subtle against its neighbours; a hairline edge fixes that.
  headerRight = null,     // optional element rendered inline after title/+/✏ (e.g. Events'
                          // Future/Past toggle). The whole cluster — title, +, ✏, headerRight —
                          // is centred together within the column, never pushed to an edge.
  children
}) => {
  // Column header label scales 14px@1200 -> 20px@1800+ (LAYOUT.md's column-header row, brief
  // 2026-09-05) - reduction-only: 20px (the existing Michael-blessed size) is retained at/above
  // 1800, it only shrinks as the viewport narrows toward 1200. Own useMediaQuery() call, not a
  // prop, since ModeColumn is reused well beyond the two front pages (ArticleDetail, GroupHomePanel,
  // DirectoryNav, etc.) and prop-drilling windowWidth would need touching every call site.
  const { windowWidth } = useMediaQuery();
  const headerFontSize = clampScale(windowWidth, { start: 1200, end: 1800, minVal: 14, maxVal: 20 });

  // Body: either the standard independent-scroll container (Events/Swap) or, when the child
  // owns its scroll (Chat), the children rendered directly into the flex column.
  const body = scrollBody ? (
    <div style={{
      flex: 1,
      minHeight: 0,
      overflowY: 'auto',
      overflowX: 'hidden',
      scrollbarWidth: 'thin',
      paddingRight: '2px',
      opacity: bodyOpacity
    }}>
      {isLoading ? (
        <div style={{
          padding: '32px 16px',
          textAlign: 'center',
          fontFamily: 'OpenSauceSans, sans-serif',
          fontSize: '13px',
          color: '#6b7280',
          lineHeight: '1.5'
        }}>
          {emptyLabel}
        </div>
      ) : isEmpty ? (
        <div style={{
          padding: '32px 16px',
          textAlign: 'center',
          fontFamily: 'OpenSauceSans, sans-serif',
          fontSize: '13px',
          color: '#6b7280',
          lineHeight: '1.5'
        }}>
          {emptyLabel}
        </div>
      ) : (
        children
      )}
    </div>
  ) : (
    children
  );

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      minHeight: 0,
      minWidth: 0,
      position: 'relative',  // no-op for Chat/Events; anchors Swap's overlay
      background: containerBg || 'transparent',
      border: containerBorder || 'none',
      borderRadius: containerBg ? '10px' : 0,
      padding: containerBg ? '6px' : 0,
      boxSizing: 'border-box'
    }}>
      {/* Column header — hidden on mobile FP (Row 2 carries the create button). Always centred
          within the column — title/+/headerRight (e.g. Events' toggle) sit as one centred group,
          not pushed to opposite edges. */}
      {!hideColumnHeader && (
        <div style={{
          padding: '2px 4px 6px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          flexShrink: 0
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{
              fontFamily: 'OpenSauceSans, sans-serif',
              fontWeight: '700',
              fontSize: `${headerFontSize}px`,
              color: themeColor,
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              {title}
            </span>
            {onAdd && (
              <button
                onClick={onAdd}
                title={addLabel}
                style={{
                  background: themeColor,
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  width: '22px',
                  height: '22px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  padding: 0,
                  transition: 'opacity 0.15s'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.8'; }}
                onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
                onTouchStart={(e) => { e.currentTarget.style.opacity = '0.8'; }}
                onTouchEnd={(e) => { e.currentTarget.style.opacity = '1'; }}
              >
                <Plus size={14} />
              </button>
            )}
            {/* RESERVED ✏ slot — wired in a later phase, renders nothing now */}
            {editAction && (
              <button
                onClick={editAction}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  display: 'flex',
                  alignItems: 'center',
                  color: themeColor
                }}
              >
                <Pencil size={14} />
              </button>
            )}
          </div>
          {headerRight}
        </div>
      )}

      {body}

      {/* Optional pinned footer — sits below the scroll body, never scrolls (Directory branding) */}
      {footer && (
        <div style={{ flexShrink: 0 }}>
          {footer}
        </div>
      )}

      {overlay}
    </div>
  );
};

export default ModeColumn;
